// components/layout/Footer.tsx
'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { FiInstagram, FiFacebook } from 'react-icons/fi';
import { NEIGHBORHOODS } from '@/lib/delivery/neighborhoods';
import { NEIGHBORHOOD_CONTENT } from '@/lib/delivery/neighborhood-content';
import { RETURN_POLICY_PATH } from '@/lib/return-policy';

// Only link to neighborhoods whose leaf pages have shipped content —
// generateStaticParams in app/delivery/[slug]/page.tsx is keyed off
// NEIGHBORHOOD_CONTENT with dynamicParams=false, so anything else 404s.
const FOOTER_NEIGHBORHOODS = NEIGHBORHOODS.filter((n) => n.slug in NEIGHBORHOOD_CONTENT);

// 환경 변수에서 reCAPTCHA 사이트 키 가져오기
const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

export const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [subscribeMessage, setSubscribeMessage] = useState('');
  // Only read on submit, so it doesn't need to trigger a render.
  const recaptchaLoadedRef = useRef(false);
  
  // 허니팟 필드를 위한 ref
  const honeypotRef = useRef<HTMLInputElement>(null);
  
  // 폼 제출 시간 추적 (너무 빠른 제출 방지) — 마운트 시점에 기록
  const formLoadTime = useRef(0);

  useEffect(() => {
    formLoadTime.current = Date.now();

    // reCAPTCHA v3 스크립트 로드
    if (RECAPTCHA_SITE_KEY && typeof window !== 'undefined') {
      // 이미 로드되었는지 확인
      if ((window as any).grecaptcha) {
        recaptchaLoadedRef.current = true;
      } else {
        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
        script.async = true;
        script.onload = () => { recaptchaLoadedRef.current = true; };
        document.body.appendChild(script);
      }
    }
    
    return undefined;
  }, []);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 이메일 유효성 검사
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setSubscribeStatus('error');
      setSubscribeMessage('Please enter a valid email address');
      return;
    }
    
    // 허니팟 체크 (봇이 숨겨진 필드를 채웠는지 확인)
    if (honeypotRef.current?.value) {
      // 봇에게는 성공한 것처럼 보이게 하지만 실제로는 아무것도 하지 않음
      setSubscribeStatus('success');
      setSubscribeMessage('Thank you for subscribing!');
      setEmail('');
      return;
    }
    
    // 너무 빠른 제출 방지 (3초 미만)
    const submitTime = Date.now() - formLoadTime.current;
    if (submitTime < 3000) {
      setSubscribeStatus('error');
      setSubscribeMessage('Please wait a moment before submitting');
      return;
    }
    
    setIsSubscribing(true);
    
    try {
      let recaptchaToken = '';
      
      // reCAPTCHA 토큰 생성 (사용 가능한 경우)
      if (recaptchaLoadedRef.current && (window as any).grecaptcha && RECAPTCHA_SITE_KEY) {
        try {
          recaptchaToken = await new Promise((resolve) => {
            (window as any).grecaptcha.ready(() => {
              (window as any).grecaptcha
                .execute(RECAPTCHA_SITE_KEY, { action: 'newsletter' })
                .then(resolve)
                .catch(() => resolve('')); // 실패 시 빈 문자열
            });
          });
        } catch (error) {
          console.error('reCAPTCHA error:', error);
          // reCAPTCHA 실패해도 계속 진행 (graceful degradation)
        }
      }
      
      // Shopify 연동 API 호출
      const res = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          // 허니팟 필드가 채워졌는지 헤더로 전달
          'X-Honeypot': honeypotRef.current?.value || ''
        },
        body: JSON.stringify({ 
          email,
          recaptchaToken: recaptchaToken || undefined // 빈 문자열 대신 undefined
        }),
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        console.error('Newsletter signup error:', res.status, data);
        throw new Error(data.error || 'Failed to subscribe. Please try again later.');
      }
      
      setSubscribeStatus('success');
      setSubscribeMessage('Thank you for subscribing!');
      setEmail('');
      
      // 성공 후 폼 로드 시간 리셋
      formLoadTime.current = Date.now();
      
    } catch (error: any) {
      setSubscribeStatus('error');
      setSubscribeMessage(error.message || 'Failed to subscribe. Please try again later.');
    } finally {
      setIsSubscribing(false);
      // 5초 후 상태 초기화
      setTimeout(() => {
        setSubscribeStatus('idle');
        setSubscribeMessage('');
      }, 5000);
    }
  };

  return (
    <>
      <footer className="bg-gray-950 pt-10 pb-6">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* 브랜드/로고 — matches the header logo in components/header/TopBar.tsx */}
          <div>
            <Link
              href="/"
              className="inline-flex items-center gap-2 group"
              aria-label="Amsterwine — Home"
            >
              <div className="relative w-8 h-8">
                <div className="absolute inset-0 bg-gradient-to-br from-[#febd69] to-[#f3a847] rounded-lg transform rotate-45 group-hover:rotate-90 transition-transform duration-500" />
                <div className="absolute inset-0.5 bg-gray-950 rounded-lg flex items-center justify-center">
                  <span className="text-[#febd69] font-bold text-lg">A</span>
                </div>
              </div>
              <div className="flex flex-col leading-none">
                <span className="text-xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  Amsterwine
                </span>
                <span className="mt-0.5 text-[11px] text-[#febd69] font-medium tracking-wider lowercase">
                  wine &amp; spirits company
                </span>
              </div>
            </Link>
            <p className="text-sm text-gray-400 mt-3 max-w-xs">
              Your premium destination for the finest wines &amp; spirits.
            </p>
            <div className="flex gap-3 mt-3">
              <a href="https://www.instagram.com/amsterwine/" className="text-gray-400 hover:text-white transition" target="_blank" rel="noopener noreferrer"><FiInstagram className="w-5 h-5" /></a>
              <a href="https://www.facebook.com/amsterwine" className="text-gray-400 hover:text-white transition" target="_blank" rel="noopener noreferrer"><FiFacebook className="w-5 h-5" /></a>
            </div>
          </div>
          {/* 매장 정보 */}
          <div className="flex flex-col gap-1 min-w-[160px]">
            <span className="text-xs font-semibold text-gray-300 mb-1">Store Info</span>
            <span className="text-sm text-gray-400">475 9th Avenue, Space A</span>
            <span className="text-sm text-gray-400">New York, NY 10018</span>
            <a 
              href="tel:+16468385011" 
              className="text-sm text-gray-400 hover:text-purple-400 transition-colors duration-200"
            >
              Phone: (646) 838-5011
            </a>
            <a 
              href="mailto:support@amsterwine.com" 
              className="text-sm text-gray-400 hover:text-purple-400 transition-colors duration-200"
            >
              support@amsterwine.com
            </a>
            <span className="text-xs font-semibold text-gray-300 mt-2">Store Hours</span>
            <span className="text-xs text-gray-400">Sunday 12pm to 8:50pm</span>
            <span className="text-xs text-gray-400">Mon-Thu 12pm to 9:50pm</span>
            <span className="text-xs text-gray-400">Fri-Sat 11am to 10:50pm</span>
          </div>
          {/* 네비게이션 */}
          <div className="flex flex-col gap-1 min-w-[120px]">
            <span className="text-xs font-semibold text-gray-300 mb-1">Quick Links</span>
            <Link href="/info/about" className="text-xs text-gray-400 hover:text-white transition">About Us</Link>
            <Link href="/info/contact" className="text-xs text-gray-400 hover:text-white transition">Contact</Link>
            <Link href="/info/faq" className="text-xs text-gray-400 hover:text-white transition">FAQ</Link>
            <Link href={RETURN_POLICY_PATH} className="text-xs text-gray-400 hover:text-white transition">Return Policy</Link>
            <Link href="/info/delivery-zone" className="text-xs text-gray-400 hover:text-white transition">Delivery Zone</Link>
            <Link href="/info/shipping" className="text-xs text-gray-400 hover:text-white transition">Shipping Info</Link>
            <Link href="/info/gallery" className="text-xs text-gray-400 hover:text-white transition">Gallery</Link>
          </div>
          {/* 동네별 배송 페이지 */}
          <div className="flex flex-col gap-1 min-w-[140px]">
            <span className="text-xs font-semibold text-gray-300 mb-1">Delivery Areas</span>
            {FOOTER_NEIGHBORHOODS.map((n) => (
              <Link
                key={n.slug}
                href={`/delivery/${n.slug}`}
                className="text-xs text-gray-400 hover:text-white transition"
              >
                {n.name}
              </Link>
            ))}
            <Link
              href="/delivery"
              className="text-xs text-gray-400 hover:text-white transition mt-1 font-medium"
            >
              All {FOOTER_NEIGHBORHOODS.length} Areas →
            </Link>
          </div>
          {/* Newsletter 구독 폼 */}
          <div className="flex flex-col gap-2 min-w-[180px]">
            <span className="text-xs font-semibold text-gray-300 mb-1">Join Our Wine Community</span>
            <p className="text-xs text-gray-400 mb-2">
              Subscribe to receive exclusive offers, wine pairing tips, and early access to new collections.
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col gap-2">
              <div className="flex gap-1">
                <label htmlFor="newsletter-email" className="sr-only">Email</label>
                <input
                  type="email"
                  id="newsletter-email"
                  name="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 px-3 py-2 text-sm rounded-l bg-gray-800 text-white border border-gray-700 focus:outline-hidden focus:border-purple-500 transition-colors"
                  disabled={isSubscribing || subscribeStatus === 'success'}
                  autoComplete="email"
                />
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white rounded-r bg-purple-600 hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubscribing || subscribeStatus === 'success'}
                >
                  {isSubscribing ? "Subscribing..." : "Join"}
                </button>
              </div>
              
              {/* 허니팟 필드 (봇 트랩) - 시각적으로 완전히 숨김 */}
              <input
                ref={honeypotRef}
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: '-9999px',
                  width: '1px',
                  height: '1px',
                  overflow: 'hidden'
                }}
              />
              
              {subscribeStatus !== 'idle' && (
                <p className={`text-xs ${subscribeStatus === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                  {subscribeMessage}
                </p>
              )}
            </form>
            
            {/* reCAPTCHA 고지 (reCAPTCHA가 활성화된 경우에만 표시) */}
            {RECAPTCHA_SITE_KEY && (
              <p className="text-[10px] text-gray-600 mt-1 leading-tight">
                This site is protected by reCAPTCHA and the Google{' '}
                <a href="https://policies.google.com/privacy" className="underline hover:text-gray-500" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a> and{' '}
                <a href="https://policies.google.com/terms" className="underline hover:text-gray-500" target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </a> apply.
              </p>
            )}
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 mt-8 border-t border-gray-800 pt-4 flex flex-col md:flex-row items-center justify-between gap-2">
          <p className="text-xs text-gray-500">&copy; 2024 Amster Wine. All rights reserved.</p>
          <div className="flex gap-4">
            <Link href="/info/privacy" className="text-xs text-gray-500 hover:text-white transition">Privacy Policy</Link>
            <Link href="/info/terms" className="text-xs text-gray-500 hover:text-white transition">Terms of Service</Link>
            <Link href={RETURN_POLICY_PATH} className="text-xs text-gray-500 hover:text-white transition">Return &amp; Refund Policy</Link>
            <Link href="/info/shipping" className="text-xs text-gray-500 hover:text-white transition">Shipping Info</Link>
            <Link href="/info/sms" className="text-xs text-gray-500 hover:text-white transition">Text Messages</Link>
          </div>
        </div>
      </footer>
    </>
  );
};
